import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth.context'
import { useRedirectUrl } from '../../hooks/useRedirectUrl'
import { useToast } from '../../hooks/use-toast'
import StepsIndicator from '../steps-indicator'

interface TOTPRegistrationProps {
  onComplete?: () => void
  onCancel?: () => void
  redirectTo?: string
}

export function TOTPRegistration({ onComplete, onCancel, redirectTo }: TOTPRegistrationProps) {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const [finalRedirectTo, setFinalRedirectTo] = useState<string>('/dashboard')
  const navigate = useNavigate()
  const location = useLocation()
  const { getStoredUrl, clearStoredUrl } = useRedirectUrl()
  const { setHasTOTPRegistered, refreshSession } = useAuth()
  const { toast } = useToast()
  
  // Determine the redirect target from URL params or localStorage
  useEffect(() => {
    // First check URL params
    const params = new URLSearchParams(location.search)
    const redirectParam = params.get('redirect')
    
    if (redirectParam) {
      setFinalRedirectTo(redirectParam)
      console.log('[TOTPRegistration] Using redirect from URL param:', redirectParam)
    } else if (redirectTo) {
      // Then check props
      setFinalRedirectTo(redirectTo)
      console.log('[TOTPRegistration] Using redirect from props:', redirectTo)
    } else {
      // Finally check localStorage
      const storedUrl = getStoredUrl()
      if (storedUrl) {
        setFinalRedirectTo(storedUrl)
        console.log('[TOTPRegistration] Using redirect from localStorage:', storedUrl)
      } else {
        // Default to dashboard
        setFinalRedirectTo('/dashboard')
        console.log('[TOTPRegistration] Using default redirect: /dashboard')
      }
    }
  }, [location.search, redirectTo, getStoredUrl])

  const setupTOTP = async () => {
    try {
      console.log('[TOTPRegistration] Setting up TOTP')
      
      // First ensure we have a valid session
      const { data: { session } } = await supabase.auth.getSession()
      console.log('[TOTPRegistration] Current session:', session ? 'Session exists' : 'No session')
      
      if (!session) {
        console.log('[TOTPRegistration] No active session, trying to refresh session')
        await refreshSession()
      }
      
      const { data: { user } } = await supabase.auth.getUser()
      console.log('[TOTPRegistration] User:', user ? `User found: ${user.email}` : 'No user found')
      
      if (!user?.email) throw new Error('User email not found')

      console.log('[TOTPRegistration] Enrolling in MFA')
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Club Portal - ${user.email}`
      })
      
      if (error) {
        console.error('[TOTPRegistration] MFA enrollment error:', error)
        throw error
      }
      
      if (data.totp) {
        console.log('[TOTPRegistration] MFA enrollment successful, setting QR code and secret')
        setQrCode(data.totp.qr_code)
        setSecret(data.totp.secret)
        setFactorId(data.id)
      } else {
        console.error('[TOTPRegistration] MFA enrollment response missing TOTP data')
      }
    } catch (error: any) {
      console.error('[TOTPRegistration] Error in setupTOTP:', error)
      const errorMessage = error.message || 'Failed to setup TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error Setting Up 2FA',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    console.log('[TOTPRegistration] Component mounted, setting up TOTP')
    // Add a small delay to ensure any session updates have been processed
    const timeoutId = setTimeout(() => {
      setupTOTP()
    }, 500)
    
    return () => clearTimeout(timeoutId)
  }, [])

  const handleRefreshQR = async () => {
    setFormError('')
    await setupTOTP()
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })
      
      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })
      
      if (verifyError) throw verifyError

      // Refresh the session to ensure we have the latest token
      await refreshSession()
      setHasTOTPRegistered(true)
      clearStoredUrl()

      toast({
        title: 'Success',
        description: 'Two-factor authentication has been set up successfully.',
      })

      if (onComplete) {
        onComplete()
      }

      // Short delay before redirect
      setTimeout(() => {
        if (finalRedirectTo.startsWith('http://') || finalRedirectTo.startsWith('https://')) {
          window.location.href = finalRedirectTo
        } else {
          console.log('[TOTPRegistration] Redirecting to:', finalRedirectTo)
          navigate(finalRedirectTo, { replace: true })
        }
      }, 1500)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      // If we have a stored URL, navigate there, otherwise go to dashboard
      const storedUrl = getStoredUrl()
      if (storedUrl && storedUrl !== '/settings') {
        navigate(storedUrl)
      } else {
        navigate('/dashboard')
      }
    }
  }

  return (
    <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8">
      <div className="flex flex-col items-center mx-8 gap-2 mb-6">
        <h1 className="font-semibold text-lavender-900 text-[40px] leading-[52px]">Setup 2FA</h1>
        <p className="font-[400] text-[18px] leading-[32px] text-[#666666]">
          Secure your account with two-factor authentication
        </p>
      </div>
      <StepsIndicator currentStep={2} />
      <div className="space-y-4 mt-8">
        <div className="text-center mb-6">
          {qrCode && (
            <div className="mb-6 bg-white p-4 rounded-lg inline-block">
              <img src={qrCode} alt="QR Code" className="mx-auto" />
              <Button
                type="button"
                variant="outline"
                className="mt-4 rounded-3xl"
                onClick={handleRefreshQR}
                disabled={isLoading}
              >
                Refresh QR Code
              </Button>
            </div>
          )}
          {secret && (
            <div className="mb-6">
              <p className="text-sm text-lavender-900">Manual entry code:</p>
              <code className="block p-2 bg-white rounded-lg text-sm mt-2 font-mono text-lavender-900">{secret}</code>
            </div>
          )}
          <p className="mb-6 text-[#666666] text-md">
            Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.) or enter the secret key manually
          </p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-lavender-900">Verification Code *</p>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="Enter 6-digit code from your authenticator app"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              required
              disabled={isLoading}
              className="w-full rounded-3xl"
            />
          </div>
          {formError && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-3xl"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 rounded-3xl bg-[#354297] hover:bg-[#354297]"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  )
}